import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BuylistItem, BuylistCartItem, BuylistPaymentMethod } from '../types';

export const BUYLIST_STORAGE_KEY = 'buylistCart';

interface BuylistState {
  items: BuylistCartItem[];
  totalQuantity: number;
  totalAmount: number;
  paymentMethod: BuylistPaymentMethod | null;
}

const defaultState: BuylistState = {
  items: [],
  totalQuantity: 0,
  totalAmount: 0,
  paymentMethod: null,
};

const loadBuylistState = (): BuylistState => {
  try {
    const stored = localStorage.getItem(BUYLIST_STORAGE_KEY);
    if (!stored) return defaultState;

    const parsed = JSON.parse(stored);
    if (
      !Array.isArray(parsed.items) ||
      typeof parsed.totalQuantity !== 'number' ||
      typeof parsed.totalAmount !== 'number'
    ) {
      localStorage.removeItem(BUYLIST_STORAGE_KEY);
      return defaultState;
    }

    return parsed as BuylistState;
  } catch {
    localStorage.removeItem(BUYLIST_STORAGE_KEY);
    return defaultState;
  }
};

const initialState: BuylistState = loadBuylistState();

const buylistSlice = createSlice({
  name: 'buylist',
  initialState,
  reducers: {
    addToBuylist: (state, action: PayloadAction<{ item: BuylistItem; quantity: number }>) => {
      const { item, quantity } = action.payload;
      
      // Don't allow adding more than maxQuantity
      const actualQuantity = Math.min(quantity, item.maxQuantity);
      
      const existingItem = state.items.find(cartItem => cartItem.item.buylistID === item.buylistID);
      
      if (existingItem) {
        // Calculate new quantity ensuring it doesn't exceed maxQuantity
        const newQuantity = Math.min(existingItem.quantity + actualQuantity, item.maxQuantity);
        const quantityDifference = newQuantity - existingItem.quantity;
        
        existingItem.quantity = newQuantity;
        state.totalQuantity += quantityDifference;
        state.totalAmount += item.buyPrice * quantityDifference;
      } else {
        state.items.push({ item, quantity: actualQuantity });
        state.totalQuantity += actualQuantity;
        state.totalAmount += item.buyPrice * actualQuantity;
      }
    },
    
    removeFromBuylist: (state, action: PayloadAction<number>) => {
      const buylistId = action.payload;
      const existingItem = state.items.find(item => item.item.buylistID === buylistId);
      
      if (existingItem) {
        state.totalQuantity -= existingItem.quantity;
        state.totalAmount -= existingItem.item.buyPrice * existingItem.quantity;
        state.items = state.items.filter(item => item.item.buylistID !== buylistId);
      }
    },
    
    updateBuylistQuantity: (state, action: PayloadAction<{ buylistId: number; quantity: number }>) => {
      const { buylistId, quantity } = action.payload;
      const existingItem = state.items.find(item => item.item.buylistID === buylistId);
      console.log("new quantity is: ");
      if (existingItem) {
        // Ensure the new quantity doesn't exceed maxQuantity
        const newQuantity = Math.min(quantity, existingItem.item.maxQuantity);
        const quantityDifference = newQuantity - existingItem.quantity;
        console.log("new quantity is: " + newQuantity);
        console.log("new quantityDifference is: " + quantityDifference);
        existingItem.quantity = newQuantity;
        state.totalQuantity += quantityDifference;
        state.totalAmount += existingItem.item.buyPrice * quantityDifference;
      }
    },
    
    setPaymentMethod: (state, action: PayloadAction<BuylistPaymentMethod>) => {
      state.paymentMethod = action.payload;
    },
    
    addBulkToBuylist: (state, action: PayloadAction<{ items: Array<{ item: BuylistItem; quantity: number }> }>) => {
      const { items: incomingItems } = action.payload;

      // Build a Map for O(1) lookups of existing cart items by buylistID
      const indexMap = new Map<number, number>();
      state.items.forEach((cartItem, index) => {
        indexMap.set(cartItem.item.buylistID, index);
      });

      for (const { item, quantity } of incomingItems) {
        const actualQuantity = Math.min(quantity, item.maxQuantity);
        const existingIndex = indexMap.get(item.buylistID);

        if (existingIndex !== undefined) {
          const existingItem = state.items[existingIndex];
          const newQuantity = Math.min(existingItem.quantity + actualQuantity, item.maxQuantity);
          const quantityDifference = newQuantity - existingItem.quantity;
          existingItem.quantity = newQuantity;
          state.totalQuantity += quantityDifference;
          state.totalAmount += item.buyPrice * quantityDifference;
        } else {
          state.items.push({ item, quantity: actualQuantity });
          state.totalQuantity += actualQuantity;
          state.totalAmount += item.buyPrice * actualQuantity;
          // Track new item so in-batch duplicates are handled correctly
          indexMap.set(item.buylistID, state.items.length - 1);
        }
      }
    },

    clearBuylist: (state) => {
      state.items = [];
      state.totalQuantity = 0;
      state.totalAmount = 0;
      state.paymentMethod = null;
      localStorage.removeItem(BUYLIST_STORAGE_KEY);
    },
  },
});

export const {
  addToBuylist,
  addBulkToBuylist,
  removeFromBuylist,
  updateBuylistQuantity,
  setPaymentMethod,
  clearBuylist
} = buylistSlice.actions;

export default buylistSlice.reducer;
